import { call, put, all, takeEvery, takeLatest, select } from 'redux-saga/effects'

import { showToasts } from '../../helpers/toasts'

import {
  GET_CAMPAIGN,
  getCampaignSuccess,
  getCampaignFailure,
  GET_CAMPAIGNS,
  getCampaignsSuccess,
  getCampaignsFailure,
  CREATE_CAMPAIGN,
  createCampaignSuccess,
  createCampaignFailure,
  UPDATE_CAMPAIGN,
  updateCampaignSuccess,
  updateCampaignFailure,
  DELETE_CAMPAIGN,
  deleteCampaignSuccess,
  deleteCampaignFailure,
  GET_CAMPAIGNS_GRAPHS,
  getCampaignsGraphsSuccess,
  getCampaignsGraphsFailure,
  GET_SHORT_INFO_CAMPAIGNS,
  getShortInfoCampaignsSuccess,
  getShortInfoCampaignsFailure,
  GET_CAMPAIGN_CRITERIONS,
  getCampaignCriterionsSuccess,
  getCampaignCriterionsFailure,
  GET_CAMPAIGN_AD_SCHEDULE_CRITERIONS,
  getCampaignAdScheduleCriterionsSuccess,
  getCampaignAdScheduleCriterionsFailure,
  CREATE_CAMPAIGN_CRITERIONS,
  createCampaignCriterionsSuccess,
  createCampaignScheduleCriterionsSuccess,
  createCampaignCriterionsFailure,
  deleteCampaignCriterions,
  duplicateCampaignSuccess,
  duplicateCampaignFailure,
  DUPLICATE_CAMPAIGN
} from '../actions/campaigns'
import { updateUser } from '../actions/user'
import {
  deleteCombinedCampaign,
  getCombinedCampaignsSuccess,
  UPDATE_COMBINED_CAMPAIGN,
  updateCombinedCampaign
} from '../actions/combinedData'

import { selectedAdAccountsSelector } from '../selectors/adAccounts'
import { campaignsIsLoadingSelector } from '../selectors/campaigns'
import { combinedCampaignsSelector } from '../selectors/combinedData'

import {
  getCampaignService,
  getCampaignsService,
  createCampaignService,
  deleteCampaignService,
  updateCampaignService,
  getCampaignsGraphsService,
  getShortInfoCampaignsService,
  getCampaignCriterionsService,
  getCampaignAdScheduleCriterionsService,
  createCampaignCriterionsService,
  createCampaignCriterionScheduleService,
  createCampaignCriterionProximityService,
  duplicateCampaignService
} from '../services/campaigns'
import { criterionTypes } from '../../constants/campaigns'
import { TOAST_TYPE } from '../../constants/other'

function* campaignsWatcher() {
  yield all([
    // PLOP_APPEND_PATTERN_ANCHOR_WATCHER
    takeEvery(GET_CAMPAIGN, getCampaignWorker),
    // take latest campaigns helps to avoid representing campaigns which are not actual
    takeLatest(GET_CAMPAIGNS, getCampaignsWorker),
    takeEvery(CREATE_CAMPAIGN, createCampaignWorker),
    takeEvery(UPDATE_CAMPAIGN, updateCampaignWorker),
    takeEvery(DELETE_CAMPAIGN, deleteCampaignWorker),
    takeLatest(GET_CAMPAIGNS_GRAPHS, getCampaignsGraphsWorker),
    takeEvery(GET_SHORT_INFO_CAMPAIGNS, getShortInfoCampaignsWorker),
    takeEvery(GET_CAMPAIGN_CRITERIONS, getCampaignCriterionsWorker),
    takeEvery(GET_CAMPAIGN_AD_SCHEDULE_CRITERIONS, getCampaignAdScheduleCriterionsWorker),
    takeEvery(CREATE_CAMPAIGN_CRITERIONS, createCampaignCriterionsWorker),
    takeEvery(DUPLICATE_CAMPAIGN, duplicateCampaignWorker)
  ])
}

// PLOP_APPEND_PATTERN_ANCHOR_WORKER
function* getCampaignWorker({ params, platform }) {
  try {
    const response = yield call(getCampaignService, platform, params)
    yield put(getCampaignSuccess(response))
  } catch (e) {
    yield put(getCampaignFailure(e))
  }
}

function* getCampaignsWorker({ params, platform }) {
  try {
    const response = yield call(getCampaignsService, params, platform)

    yield put(getCampaignsSuccess(response))
  } catch (e) {
    yield put(getCampaignsFailure(e))
  }
}

function* createCampaignWorker({ data, platform, sagaActions }) {
  try {
    const { campaignData, updatedSettings, userId } = data

    const { pushToCombinedCampaignsList } = sagaActions

    const selectedAdAccounts = yield select(selectedAdAccountsSelector)

    const createdCampaignData = yield call(createCampaignService, campaignData, platform)

    yield put(createCampaignSuccess(createdCampaignData))

    if (updatedSettings) {
      yield put(updateUser(updatedSettings, userId))
    }

    if (pushToCombinedCampaignsList) {
      const combinedCampaigns = yield select(combinedCampaignsSelector)

      const { campaigns = [] } = combinedCampaigns

      const newCampaign = {
        ...createdCampaignData,
        currency_symbol: selectedAdAccounts[platform]['currency_symbol'],
        // duplicate field using another name, which we use in FE logic
        ad_account_id: createdCampaignData.account || selectedAdAccounts[platform]?.id
      }

      yield put(
        getCombinedCampaignsSuccess({
          ...combinedCampaigns,
          campaigns: [newCampaign, ...campaigns]
        })
      )
    }
  } catch (e) {
    if (e.errors?.performance_goal_type) {
      showToasts({
        type: TOAST_TYPE.error,
        message: e.errors?.performance_goal_type
      })
    }

    yield put(createCampaignFailure(e))
  }
}

function* updateCampaignWorker({ campaignData, id, platform, updateOptions }) {
  try {
    const { updateType, requestMethod } = updateOptions

    const response = yield call(updateCampaignService, campaignData, id, platform, requestMethod)
    yield put(updateCampaignSuccess(response, platform))

    // sometimes we need to also update data inside combined campaigns store
    if (updateType === UPDATE_COMBINED_CAMPAIGN) {
      yield put(updateCombinedCampaign(response))
    }
  } catch (e) {
    yield put(updateCampaignFailure(e))
  }
}

function* duplicateCampaignWorker({ duplicateData, id, platform }) {
  try {
    const response = yield call(duplicateCampaignService, duplicateData, id, platform)
    yield put(duplicateCampaignSuccess(response))
  } catch (e) {
    yield put(duplicateCampaignFailure(e))
  }
}

function* deleteCampaignWorker({ params, sagaActions }) {
  const { id } = params
  try {
    yield call(deleteCampaignService, params)
    const { updateCombinedCampaignsList } = sagaActions

    if (updateCombinedCampaignsList) {
      yield put(deleteCombinedCampaign(id))
    }

    yield put(deleteCampaignSuccess(id))
  } catch (e) {
    yield put(deleteCampaignFailure(e))
  }
}

function* getCampaignsGraphsWorker({ data }) {
  try {
    const response = yield call(getCampaignsGraphsService, data)

    // GET_CAMPAIGNS_GRAPHS is dependent on GET_CAMPAIGNS_SUCCESS data
    // takeLatest doesn't helps in all cases i.e. as GET_CAMPAIGNS_GRAPHS is calling only after on GET_CAMPAIGNS_SUCCESS
    // i.e. there are cases when new campaigns are loading but we still represent all campaigns graphs
    // to fix this - ignore successful GET_CAMPAIGNS_GRAPHS if new campaigns are loading
    const facebookCampaignsIsLoading = yield select(campaignsIsLoadingSelector)
    if (!facebookCampaignsIsLoading) {
      yield put(getCampaignsGraphsSuccess(response))
    }
  } catch (e) {
    yield put(getCampaignsGraphsFailure(e))
  }
}

function* getShortInfoCampaignsWorker({ params }) {
  try {
    const response = yield call(getShortInfoCampaignsService, params)
    yield put(getShortInfoCampaignsSuccess(response))
  } catch (e) {
    yield put(getShortInfoCampaignsFailure(e))
  }
}

function* getCampaignCriterionsWorker({ params }) {
  try {
    const response = yield call(getCampaignCriterionsService, params)
    yield put(getCampaignCriterionsSuccess(response))
  } catch (e) {
    yield put(getCampaignCriterionsFailure(e))
  }
}

function* getCampaignAdScheduleCriterionsWorker({ params }) {
  try {
    const response = yield call(getCampaignAdScheduleCriterionsService, params)
    yield put(getCampaignAdScheduleCriterionsSuccess(response))
  } catch (e) {
    yield put(getCampaignAdScheduleCriterionsFailure(e))
  }
}

function* createCampaignCriterionsWorker({ criterionsData, criterionType, criterionsToDelete }) {
  try {
    let response

    switch (criterionType) {
      case criterionTypes.proximity:
        response = yield call(createCampaignCriterionProximityService, criterionsData)
        break
      case criterionTypes.schedule:
        response = yield call(createCampaignCriterionScheduleService, criterionsData)
        break
      default:
        response = yield call(createCampaignCriterionsService, criterionsData)
    }

    if (criterionType === criterionTypes.schedule) {
      yield put(createCampaignScheduleCriterionsSuccess(response))
    } else {
      yield put(createCampaignCriterionsSuccess(response))
    }

    if (criterionsToDelete.length) {
      // filter inner store of criterions when using remove_operations in create request
      yield put(deleteCampaignCriterions(criterionsToDelete))
    }
  } catch (e) {
    yield put(createCampaignCriterionsFailure(e))
  }
}

export default campaignsWatcher
